/**
 * useOrderFilters Hook
 * Manages filter state with URL synchronization for persistence and sharing
 * Handles browser back/forward navigation
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  OrderFilters,
  FilterUrlParams,
  PaginationState,
} from '../types/filter.types';
import type { OrderStatus } from '@/types/order';

// ============================================
// DEFAULT VALUES
// ============================================

/**
 * Default filter state
 * Note: Backend API accepts single 'status' parameter, not 'statuses' array
 * Frontend maintains statuses array for UI, but converts to single status for API calls
 */
const DEFAULT_FILTERS: OrderFilters = {
  searchTerm: '',
  statuses: [],
  orderDateRange: { from: null, to: null },
  expectedDeliveryDateRange: { from: null, to: null },
  actualDeliveryDateRange: { from: null, to: null },
  amountRange: { min: null, max: null },
  customerType: 'ALL',
  location: { state: null, city: null },
};

/**
 * Default pagination state
 */
const DEFAULT_PAGINATION: PaginationState = {
  page: 1,
  pageSize: 25,
};

// ============================================
// URL ENCODING/DECODING UTILITIES
// ============================================

/**
 * Convert filters to URL params (short names for concise URLs)
 */
function filtersToUrlParams(
  filters: OrderFilters,
  pagination: PaginationState
): FilterUrlParams {
  const params: FilterUrlParams = {};

  // Search term
  if (filters.searchTerm) params.q = filters.searchTerm;

  // Statuses (comma-separated)
  if (filters.statuses.length > 0) {
    params.s = filters.statuses.join(',');
  }

  // Order date range
  if (filters.orderDateRange.from) params.odf = filters.orderDateRange.from;
  if (filters.orderDateRange.to) params.odt = filters.orderDateRange.to;

  // Expected delivery date range
  if (filters.expectedDeliveryDateRange.from) {
    params.edf = filters.expectedDeliveryDateRange.from;
  }
  if (filters.expectedDeliveryDateRange.to) {
    params.edt = filters.expectedDeliveryDateRange.to;
  }

  // Actual delivery date range
  if (filters.actualDeliveryDateRange.from) {
    params.adf = filters.actualDeliveryDateRange.from;
  }
  if (filters.actualDeliveryDateRange.to) {
    params.adt = filters.actualDeliveryDateRange.to;
  }

  // Amount range
  if (filters.amountRange.min !== null) params.min = filters.amountRange.min.toString();
  if (filters.amountRange.max !== null) params.max = filters.amountRange.max.toString();

  // Customer type
  if (filters.customerType !== 'ALL') params.ct = filters.customerType;

  // Location
  if (filters.location.state) params.st = filters.location.state;
  if (filters.location.city) params.city = filters.location.city;

  // Pagination
  if (pagination.page !== 1) params.page = pagination.page.toString();
  if (pagination.pageSize !== 25) params.size = pagination.pageSize.toString();

  return params;
}

/**
 * Parse URL params to filters
 */
function urlParamsToFilters(params: URLSearchParams): {
  filters: OrderFilters;
  pagination: PaginationState;
} {
  const filters: OrderFilters = {
    searchTerm: params.get('q') || '',
    statuses: params.get('s')
      ? (params.get('s')!.split(',') as OrderStatus[])
      : [],
    orderDateRange: {
      from: params.get('odf'),
      to: params.get('odt'),
    },
    expectedDeliveryDateRange: {
      from: params.get('edf'),
      to: params.get('edt'),
    },
    actualDeliveryDateRange: {
      from: params.get('adf'),
      to: params.get('adt'),
    },
    amountRange: {
      min: params.get('min') ? parseFloat(params.get('min')!) : null,
      max: params.get('max') ? parseFloat(params.get('max')!) : null,
    },
    customerType: (params.get('ct') as 'B2B' | 'B2C' | 'ALL') || 'ALL',
    location: {
      state: params.get('st'),
      city: params.get('city'),
    },
  };

  const pagination: PaginationState = {
    page: params.get('page') ? parseInt(params.get('page')!, 10) : 1,
    pageSize: params.get('size') ? parseInt(params.get('size')!, 10) : 25,
  };

  return { filters, pagination };
}

// ============================================
// HOOK
// ============================================

export interface UseOrderFiltersReturn {
  filters: OrderFilters;
  pagination: PaginationState;
  updateFilters: (newFilters: Partial<OrderFilters>) => void;
  updatePagination: (newPagination: Partial<PaginationState>) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
}

/**
 * Hook to manage order filters with URL synchronization
 */
export function useOrderFilters(): UseOrderFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const initialState = urlParamsToFilters(searchParams);
  const [filters, setFilters] = useState<OrderFilters>(initialState.filters);
  const [pagination, setPagination] = useState<PaginationState>(
    initialState.pagination
  );

  // Sync filters to URL when they change
  useEffect(() => {
    const urlParams = filtersToUrlParams(filters, pagination);
    const newSearchParams = new URLSearchParams();

    // Build search params from urlParams
    Object.entries(urlParams).forEach(([key, value]) => {
      if (value !== undefined) {
        newSearchParams.set(key, value);
      }
    });

    setSearchParams(newSearchParams, { replace: true });
  }, [filters, pagination, setSearchParams]);

  // Listen to browser back/forward (popstate)
  useEffect(() => {
    function handlePopState() {
      const params = new URLSearchParams(window.location.search);
      const { filters: newFilters, pagination: newPagination } =
        urlParamsToFilters(params);
      setFilters(newFilters);
      setPagination(newPagination);
    }

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Update filters
  const updateFilters = useCallback((newFilters: Partial<OrderFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    // Reset to page 1 when filters change
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Update pagination
  const updatePagination = useCallback(
    (newPagination: Partial<PaginationState>) => {
      setPagination((prev) => ({ ...prev, ...newPagination }));
    },
    []
  );

  // Reset filters to default
  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setPagination(DEFAULT_PAGINATION);
  }, []);

  // Check if any filters are active
  const hasActiveFilters =
    filters.searchTerm !== '' ||
    filters.statuses.length > 0 ||
    filters.orderDateRange.from !== null ||
    filters.orderDateRange.to !== null ||
    filters.expectedDeliveryDateRange.from !== null ||
    filters.expectedDeliveryDateRange.to !== null ||
    filters.actualDeliveryDateRange.from !== null ||
    filters.actualDeliveryDateRange.to !== null ||
    filters.amountRange.min !== null ||
    filters.amountRange.max !== null ||
    filters.customerType !== 'ALL' ||
    filters.location.state !== null ||
    filters.location.city !== null;

  return {
    filters,
    pagination,
    updateFilters,
    updatePagination,
    resetFilters,
    hasActiveFilters,
  };
}
